import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { BasePermissionMenu } from "app/based/baseApiModel/basePermissionMenu/BasePermissionMenu";
import { ApiDocumentInjection } from "features/apiDocumentFeature/di/ApiDocumentInjection";
import { ApiDocumentRequestEntity } from "features/apiDocumentFeature/domain/entities/ApiDocumentRequestEntity";
import { OverviewServiceItem } from "features/generals/domain/entities/overviewServices/OverviewServiceResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { ServiceEnum } from "utils/enums/ServiceEnum";
import { strings } from "utils/localizedStrings";
import { IApiDocumentObservedState } from "../interfaces/IApiDocument";
import { CreateFileRequestEntity } from "features/apiDocumentFeature/domain/entities/CreateFileRequestEntity";
import { DeleteFileRequestEntity } from "features/apiDocumentFeature/domain/entities/DeleteFileRequestEntity";

// ─── Input ────────────────────────────────────────────────────────────────────

class ApiDocumentInput implements Disposable {
  public fetchDoc = new BehaviorSubject<ApiDocumentRequestEntity>({});
  public fetchServices = new BehaviorSubject<any>({});
  public uploadFIle = new BehaviorSubject<CreateFileRequestEntity>({});
  public deleteFile = new BehaviorSubject<DeleteFileRequestEntity>({});

  [Symbol.dispose](): void {
    this.fetchDoc.complete();
    this.fetchServices.complete();
    this.uploadFIle.complete();
    this.deleteFile.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class ApiDocumentOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");
  public onUploadSuccess = new BehaviorSubject<string>("");
  public onDeleteSuccess = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onUploadSuccess.complete();
    this.onDeleteSuccess.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class ApiDocumentViewModel extends BasedObservedViewModel<
  ApiDocumentInput,
  ApiDocumentOutput,
  IApiDocumentObservedState
> {
  private useCase = ApiDocumentInjection.getUseCase();

  protected override createObservedState(): IApiDocumentObservedState {
    return {
      loading: false,
      list: [],
      serviceOptions: [],
      uploadLoading: false,
      deleteLoading: false,
    };
  }

  constructor() {
    super(new ApiDocumentInput(), new ApiDocumentOutput());
  }

  private getMenuSource(): BasePermissionMenu[] {
    const menuConfig = this.context?.menuConfig?.menu;
    if (Array.isArray(menuConfig) && menuConfig.length > 0) {
      return menuConfig;
    }

    const userMenu = this.context?.userMenu;
    if (Array.isArray(userMenu) && userMenu.length > 0) {
      return userMenu;
    }

    const cachedMenu = productCache.getCache(CacheKeyEnum.userMenu);
    return Array.isArray(cachedMenu) ? cachedMenu : [];
  }

  private findTransactionHistoryMenu(
    menus: BasePermissionMenu[],
  ): BasePermissionMenu | undefined {
    for (const menu of menus) {
      const componentName = menu?.componentName
        ?.replace(/[-_\s]/g, "")
        .toLowerCase();

      if (componentName === "transactionhistory") {
        return menu;
      }

      const matchedMenu = this.findTransactionHistoryMenu(menu?.sub || []);
      if (matchedMenu) return matchedMenu;
    }

    return undefined;
  }

  private filterAllowedServices(
    services: Array<Partial<OverviewServiceItem>>,
  ): OverviewServiceItem[] {
    const transactionHistoryMenu = this.findTransactionHistoryMenu(
      this.getMenuSource(),
    );
    const capabilityKeys = new Set(
      (transactionHistoryMenu?.sub || []).flatMap((menu) =>
        (menu?.capabilities || [])
          .map((capability) => capability?.key)
          .filter((key): key is string => !!key),
      ),
    );

    return services
      .filter(
        (item) =>
          !!item?.code &&
          ![ServiceEnum.ADD_BALANCE, ServiceEnum.GOGO_SIM]?.includes(
            item.code as ServiceEnum,
          ) &&
          capabilityKeys.has(`${CapabilityEnum.list}.${item.code}`),
      )
      .map((item) => ({
        name: item.name || "",
        code: item.code || "",
        displayCode: item.displayCode ?? null,
      }));
  }

  protected override transform(): void {
    // Fetch api documents
    this.skipFirstInitial(this.input.fetchDoc, async (params) => {
      try {
        this.setObservedState({ loading: true, list: [] });
        const res = await this.useCase.getApiDocuments(params);
        if (res?.data) {
          this.setObservedState({ list: res.data });
        }
      } catch (error: any) {
        console.error(error);
        this.setObservedState({ list: [] });
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error
        );
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch services
    this.skipFirstInitial(this.input.fetchServices, async (_, guard) => {
      try {
        const res = await this.useCase.getServiceList();
        guard?.runIfLatest(() => {
          const serviceOptions = this.filterAllowedServices(res || []);
          this.setObservedState({
            serviceOptions,
            ...(serviceOptions.length === 0 ? { list: [] } : {}),
          });
        });
      } catch (error: any) {
        console.error(error);
        guard?.runIfLatest(() => {
          this.setObservedState({ serviceOptions: [], list: [] });
          this.output.onError.next(
            error?.message || strings.GlobalMessage.Error,
          );
        });
      }
    });

    // Upload file
    this.skipFirstInitial(this.input.uploadFIle, async (params) => {
      try {
        this.setObservedState({ uploadLoading: true });
        const res = await this.useCase.createFile({
          ...params,
          type: 2,
          folderPath: "key/service-code",
          isPrivate: false,
          fileField: "file",
          order: 0,
        });
        if (res?.data) {
          this.output.onUploadSuccess.next(res?.message || "create");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error
        );
      } finally {
        this.setObservedState({ uploadLoading: false });
      }
    });

    // Delete file
    this.skipFirstInitial(this.input.deleteFile, async (params) => {
      if (!params?.code) return;
      try {
        this.setObservedState({ deleteLoading: true });
        const res = await this.useCase.deleteFile(params);
        if (res?.data !== undefined) {
          this.output.onDeleteSuccess.next(res?.message || "delete");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.error?.message || error?.message || strings.GlobalMessage.Error
        );
      } finally {
        this.setObservedState({ deleteLoading: false });
      }
    });
  }
}
